// Whether triangle soups close a solid, and where they are left open
// (rendering.md, *The 3D section*: the cut is filled only where it opens a closed
// solid). Pure and DOM-free (unit-tested in test/meshTopology.test.mjs).

/** A triangle soup: its corners' positions (`x, y, z` flat) and, when indexed,
 *  its index. */
export interface Soup { pos: ArrayLike<number>; index?: ArrayLike<number> | null }

/** How far a corner may stand off a side it lies on (mm): two meshings of one
 *  curve, each within its chords' sag (`MESH_SAG`, 2 mm) of it, meet at a seam
 *  with no corner in common. */
export const SEAM_MM = 3;

/** Past this many open sides a surface is taken as open as it is: pairing them
 *  is quadratic. */
const MAX_OPEN = 4000;

type P3 = [number, number, number];

/** The soups' sides no other triangle closes, each directed as its triangle runs
 *  it, between corners welded by position (to 0.01 mm). A side with corners of
 *  other open sides lying on it (within `tol`) is split at them first, so a seam
 *  between two meshings of one curve is closed. */
export function openEdges(soups: readonly Soup[], tol = SEAM_MM): { pts: P3[]; edges: Array<[number, number]> } {
  const weld = new Map<string, number>(), pts: P3[] = [];
  let edges: Array<[number, number]> = [];
  for (const { pos, index } of soups) {
    const own = new Int32Array(Math.floor(pos.length / 3));
    for (let i = 0; i < own.length; i++) {
      const x = pos[3 * i], y = pos[3 * i + 1], z = pos[3 * i + 2];
      const k = `${Math.round(x * 100)},${Math.round(y * 100)},${Math.round(z * 100)}`;
      let id = weld.get(k);
      if (id === undefined) { id = pts.length; weld.set(k, id); pts.push([x, y, z]); }
      own[i] = id;
    }
    const n = index ? index.length : own.length;
    for (let t = 0; t + 2 < n; t += 3) {
      const v = [0, 1, 2].map((e) => own[index ? index[t + e] : t + e]);
      if (v[0] === v[1] || v[1] === v[2] || v[0] === v[2]) continue;
      edges.push([v[0], v[1]], [v[1], v[2]], [v[2], v[0]]);
    }
  }
  const N = pts.length;
  const key = ([a, b]: [number, number]): number => (a < b ? a * N + b : b * N + a);
  const unpaired = (): Array<[number, number]> => {
    const sides = new Map<number, Array<[number, number]>>();
    for (const e of edges) { const k = key(e), list = sides.get(k); if (list) list.push(e); else sides.set(k, [e]); }
    const out: Array<[number, number]> = [];
    for (const list of sides.values()) if (list.length % 2) out.push(list[list.length - 1]);
    return out;
  };
  for (let pass = 0; pass < 3; pass++) {
    const open = unpaired();
    if (!open.length || open.length > MAX_OPEN || !(tol > 0)) return { pts, edges: open };
    const corners = [...new Set(open.flat())], C = new Float64Array(corners.length * 3);
    corners.forEach((v, i) => { C[3 * i] = pts[v][0]; C[3 * i + 1] = pts[v][1]; C[3 * i + 2] = pts[v][2]; });
    const splits = new Map<[number, number], number[]>();
    for (const e of open) {
      const [a, b] = e, A = pts[a], B = pts[b];
      const dx = B[0] - A[0], dy = B[1] - A[1], dz = B[2] - A[2], L2 = dx * dx + dy * dy + dz * dz;
      if (!(L2 > 0)) continue;
      const x0 = Math.min(A[0], B[0]) - tol, x1 = Math.max(A[0], B[0]) + tol, y0 = Math.min(A[1], B[1]) - tol, y1 = Math.max(A[1], B[1]) + tol;
      const z0 = Math.min(A[2], B[2]) - tol, z1 = Math.max(A[2], B[2]) + tol;
      let on: Array<[number, number]> | null = null;
      for (let i = 0; i < corners.length; i++) {
        const px = C[3 * i], py = C[3 * i + 1], pz = C[3 * i + 2];
        if (px < x0 || px > x1 || py < y0 || py > y1 || pz < z0 || pz > z1) continue;
        const v = corners[i];
        if (v === a || v === b) continue;
        const t = ((px - A[0]) * dx + (py - A[1]) * dy + (pz - A[2]) * dz) / L2;
        if (t < 0 || t > 1 || t * t * L2 <= 1e-8 || (1 - t) * (1 - t) * L2 <= 1e-8) continue;
        const qx = A[0] + t * dx - px, qy = A[1] + t * dy - py, qz = A[2] + t * dz - pz;
        if (qx * qx + qy * qy + qz * qz <= tol * tol) (on ??= []).push([t, v]);
      }
      if (on) splits.set(e, on.sort((p, q) => p[0] - q[0]).map(([, v]) => v));
    }
    if (!splits.size) return { pts, edges: open };
    const next: Array<[number, number]> = [];
    for (const e of edges) {
      const mid = splits.get(e);
      if (!mid) { next.push(e); continue; }
      const chain = [e[0], ...mid, e[1]];
      for (let i = 0; i + 1 < chain.length; i++) next.push([chain[i], chain[i + 1]]);
    }
    edges = next;
  }
  return { pts, edges: unpaired() };
}

/** Whether triangle soups TOGETHER make a closed surface ({@link openEdges}
 *  leaves none), so that their back faces show only where a section cuts them
 *  open: what the cut's fill may cover. A solid built as several pieces (one per
 *  finish) is closed as their union, never piece by piece. The soups share one
 *  frame. */
export function closedSurface(soups: readonly Soup[], tol = SEAM_MM): boolean {
  return soups.some((s) => s.pos.length >= 9) && openEdges(soups, tol).edges.length === 0;
}

/** The loops the open sides make, each walked as its triangles run it; null
 *  where they do not make simple loops (a corner two open sides leave). */
export function openLoops(soups: readonly Soup[], tol = SEAM_MM): { pts: P3[]; loops: number[][] } | null {
  const { pts, edges } = openEdges(soups, tol);
  const next = new Map<number, number>();
  for (const [a, b] of edges) {
    if (next.has(a)) return null;
    next.set(a, b);
  }
  const loops: number[][] = [], seen = new Set<number>();
  for (const s of next.keys()) {
    if (seen.has(s)) continue;
    const loop: number[] = [];
    let v: number | undefined = s;
    while (v !== undefined && !seen.has(v)) { seen.add(v); loop.push(v); v = next.get(v); }
    if (v !== s) return null;
    loops.push(loop);
  }
  return { pts, loops };
}
